<template>
    <v-dialog
        v-model="dialog"
        :max-width="options.width"
        :style="{ zIndex: options.zIndex }"
        @keydown.esc="cancel"
        @input="onDialogInput"
    >
        <v-card style="height: fit-content">
            <v-toolbar dark :color="options.color" dense flat>
                <v-toolbar-title
                    class="text-body-3 font-weight-bold grey--text"
                >
                    {{ title }}
                </v-toolbar-title>
            </v-toolbar>
            <v-card-actions class="py-0">
                <v-container style="padding-top: 0">
                    <v-row
                        dense
                        align="center"
                        class="mt-4"
                        v-if="profiles.length > 0"
                    >
                        <p
                            v-if="!isEnrolled && progress === 0"
                            class="mb-0 mr-3"
                        >
                            {{
                                $t("admin.enroll_profile.ready_to_enroll", {
                                    num: profiles.length,
                                })
                            }}
                        </p>
                    </v-row>

                    <v-row align="center" class="mb-1" dense>
                        <v-col cols="6" style="padding: 0">
                            <label class="label text-body-1">
                                {{
                                    $t(
                                        "admin.funnel_stats.selected_campaign_label",
                                    )
                                }}
                            </label>
                        </v-col>

                        <v-col cols="6" style="padding: 0">
                            <v-select
                                v-model="selectedCampaign"
                                hide-details="true"
                                :items="campaigns"
                                item-text="name"
                                item-value="id"
                                style="font-size: 14px; margin: 0; padding: 0"
                                :disabled="disableAction"
                            />
                        </v-col>
                    </v-row>

                    <v-row align="center" class="d-none mt-3" dense>
                        <label class="label label-text-field">
                            {{ $t("admin.enroll_profile.max_profile_enroll") }}
                        </label>
                        <v-col cols="3" style="padding: 0">
                            <v-text-field
                                type="number"
                                step="1"
                                class="v-input--dense col-gap"
                                style="font-size: 14px"
                                v-model="limit"
                                :disabled="disableAction"
                            />
                        </v-col>
                    </v-row>

                    <v-progress-linear
                        v-if="isLoading"
                        :value="progress"
                        color="primary"
                        height="10"
                        class="my-3"
                    />

                    <v-alert
                        v-if="isLoading"
                        type="warning"
                        title="Warning"
                        color="warning"
                        :text="true"
                        density="compact"
                        variant="outlined"
                    >
                        <p>{{ $t("admin.enroll_profile.warning") }}</p>
                    </v-alert>

                    <v-expansion-panels v-if="isEnrolled" class="mt-2">
                        <v-expansion-panel :disabled="errors.length === 0">
                            <v-expansion-panel-header>
                                <v-row
                                    v-if="isEnrolled"
                                    dense
                                    style="
                                        display: flex;
                                        flex-direction: column;
                                        gap: 0.5em;
                                    "
                                >
                                    <p
                                        v-if="successfulProfileCount > 0"
                                        class="mb-0 d-flex align-center"
                                        style="color: black"
                                    >
                                        <v-icon color="green" class="mr-1">
                                            mdi-check-circle
                                        </v-icon>
                                        {{
                                            $t(
                                                "admin.enroll_profile.profile_success",
                                                {
                                                    count: this
                                                        .successfulProfileCount,
                                                },
                                            )
                                        }}
                                    </p>
                                    <p
                                        v-if="scheduledProfileCount > 0"
                                        class="mb-0 d-flex align-center"
                                        style="color: black"
                                    >
                                        <v-icon color="#ff7600" class="mr-1">
                                            mdi-clock-outline
                                        </v-icon>
                                        {{
                                            $t(
                                                "admin.enroll_profile.profile_scheduled",
                                                {
                                                    count: this
                                                        .scheduledProfileCount,
                                                },
                                            )
                                        }}
                                    </p>
                                    <p
                                        v-if="failedProfileCount > 0"
                                        class="mb-0 d-flex align-center"
                                        style="color: black"
                                    >
                                        <v-icon color="red" class="mr-1">
                                            mdi-close-box
                                        </v-icon>
                                        {{
                                            $t(
                                                "admin.enroll_profile.profile_failed",
                                                {
                                                    count: this
                                                        .failedProfileCount,
                                                },
                                            )
                                        }}
                                    </p>
                                </v-row>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <ul class="error-list" v-if="errors.length > 0">
                                    <li
                                        v-for="error in groupedErrors"
                                        :key="error"
                                    >
                                        {{ error }}
                                    </li>
                                </ul>
                                <div class="ml-5" v-else>
                                    <p class="mb-0" style="font-size: 0.9375em">
                                        {{
                                            $t("admin.enroll_profile.no_error")
                                        }}
                                    </p>
                                </div>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>

                    <div class="dialog-btn-container">
                        <v-btn
                            v-if="!isLoading && !isEnrolled"
                            color="grey"
                            text
                            class="body-2 font-weight-bold"
                            @click.native="cancel"
                        >
                            {{ $t("common.cancel") }}
                        </v-btn>
                        <v-btn
                            color="primary"
                            class="body-2 font-weight-bold"
                            outlined
                            @click="isEnrolled ? cancel() : handleEnroll()"
                            :disabled="disableAction"
                        >
                            {{ isEnrolled ? "Close" : "Enroll" }}
                        </v-btn>
                    </div>
                </v-container>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style lang="scss" scoped>
    .error-list {
        overflow-y: scroll;
        max-height: 55px;
    }
    .label {
        color: #000000;
    }
    .label-text-field {
        margin-right: 0;
        margin-bottom: 0.8em;
    }
    .col-gap {
        margin-left: 1em;
    }
    .dialog-btn-container {
        margin-top: 1em;
        display: flex;
        justify-content: flex-end;
        gap: 0.5em;
    }
    .v-alert__icon {
        margin-right: 0;
    }
    .v-alert {
        padding: 10px 20px;
        font-size: 0.9em;
        p {
            margin: 0;
        }
    }
</style>

<script>
    import { mapAllMutations } from "@/map-state";
    import { uploadEnrollList } from "@/pages/admin/funnel-stats/utils.js";
    import groupBy from "lodash/groupBy";
    import map from "lodash/map";

    export default {
        props: {
            selectedcampaign: {
                type: String,
                default: "",
            },
            defaultmaxenrolls: {
                type: Number,
                default: 10000,
            },
            enrollprofiles: {
                type: Array,
                required: true,
            },
        },
        data() {
            return {
                isCollapsed: false,
                dialog: false,
                resolve: null,
                reject: null,
                message: null,
                title: null,
                options: {
                    color: "grey lighten-3",
                    width: 400,
                    zIndex: 200,
                },
                selectedCampaign: null,
                campaigns: [],
                limit: null,
                isLoading: false,
                isEnrolled: false,
                errors: [],
                showProgressBar: true,
                progress: 0,
                failedProfileCount: 0,
                successfulProfileCount: 0,
                scheduledProfileCount: 0,
                profiles: [],
            };
        },
        mounted() {
            this.isEnrolled = false;
            this.isLoading = false;
            this.errors = [];
            this.campaigns = [];
            this.selectedCampaign = null;
        },
        computed: {
            disableAction() {
                return !this.isEnrolled && this.isLoading;
            },
            groupedErrors() {
                return this.groupAndFormatErrors(this.errors);
            },
        },
        methods: {
            ...mapAllMutations(),
            onDialogInput(value) {
                if (!value) {
                    // The dialog is closing
                    this.resetData();
                }
            },
            async handleEnroll() {
                const type = "file";
                await uploadEnrollList(this, type);
            },
            groupAndFormatErrors(errors) {
                const groupedErrors = groupBy(errors, "message");

                const formattedErrors = map(
                    groupedErrors,
                    (errorGroup, message) => {
                        const rows = map(errorGroup, "row").join(", ");
                        return `Row ${rows}: ${message}`;
                    },
                );

                return formattedErrors;
            },
            open({ title, message, campaigns, profiles, options }) {
                this.dialog = true;
                this.title = title;
                this.campaigns = campaigns;
                this.message = message;
                this.options = Object.assign(this.options, options);
                this.profiles = profiles;
                this.resetData();

                const defaultMaxEnrollProp = this.defaultmaxenrolls;
                this.limit = defaultMaxEnrollProp;
                const selectedCampaignProp = this.selectedcampaign;
                const ALL_CAMPAIGN = "*";
                if (selectedCampaignProp === ALL_CAMPAIGN) {
                    this.selectedCampaign = campaigns[0].id;
                } else {
                    this.selectedCampaign = selectedCampaignProp;
                }

                return new Promise((resolve, reject) => {
                    this.resolve = resolve;
                    this.reject = reject;
                });
            },
            agree() {
                this.resolve(this.selectedCampaign);
                this.resetData();
                this.dialog = false;
            },
            cancel() {
                this.resolve(null);
                this.resetData();
                this.dialog = false;
            },
            resetData() {
                this.isEnrolled = false;
                this.isLoading = false;
                this.progress = 0;
                this.errors = [];
                this.successfulProfileCount = 0;
                this.failedProfileCount = 0;
            },
        },
    };
</script>
